import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import { useDeviceLayout } from './useDeviceLayout';
import {
  AUTO_PAN_EDGE_THRESHOLD_MOBILE,
  AUTO_PAN_EDGE_THRESHOLD_DESKTOP,
  AUTO_PAN_DEAD_ZONE,
  AUTO_PAN_MAX_SPEED_MOBILE,
  AUTO_PAN_MAX_SPEED_DESKTOP,
  AUTO_PAN_TURBO_ZONE,
  AUTO_PAN_TURBO_MULTIPLIER,
  AUTO_PAN_VELOCITY_THRESHOLD_MOBILE,
  AUTO_PAN_VELOCITY_THRESHOLD_DESKTOP,
  AUTO_PAN_THROTTLE_MS,
} from '../constants/mapAutoPan';

interface AutoPanOptions {
  mapRef: React.RefObject<MapRef | null>;
  isDragging: boolean;
  getPanelWidth: () => number;
  getPanelHeight: () => number;
}

interface PanSettings {
  edgeThreshold: number;
  deadZone: number;
  maxPanSpeed: number;
  turboZone: number;
  turboMultiplier: number;
  velocityThreshold: number;
  throttleMs: number;
}

export const useMapAutoPan = ({ mapRef, isDragging, getPanelWidth, getPanelHeight }: AutoPanOptions) => {
  const { isMobile } = useDeviceLayout();
  const panFrameRef = useRef<number | null>(null);
  const lastPanTimeRef = useRef<number>(0);
  const lastVelocityRef = useRef<{ x: number; y: number; time: number }>({ x: 0, y: 0, time: 0 });

  // Pan settings optimized for mobile vs desktop
  const panSettings = useMemo((): PanSettings => ({
    edgeThreshold: isMobile ? AUTO_PAN_EDGE_THRESHOLD_MOBILE : AUTO_PAN_EDGE_THRESHOLD_DESKTOP,
    deadZone: AUTO_PAN_DEAD_ZONE,
    maxPanSpeed: isMobile ? AUTO_PAN_MAX_SPEED_MOBILE : AUTO_PAN_MAX_SPEED_DESKTOP,
    turboZone: AUTO_PAN_TURBO_ZONE,
    turboMultiplier: AUTO_PAN_TURBO_MULTIPLIER,
    velocityThreshold: isMobile ? AUTO_PAN_VELOCITY_THRESHOLD_MOBILE : AUTO_PAN_VELOCITY_THRESHOLD_DESKTOP,
    throttleMs: AUTO_PAN_THROTTLE_MS,
  }), [isMobile]);

  const calculatePanSpeed = useCallback((
    distance: number,
    threshold: number,
    deadZone: number,
    maxSpeed: number,
    turboZone: number,
    turboMultiplier: number
  ): number => {
    if (distance >= threshold) return 0;

    const intensity = Math.max(0, threshold - distance - deadZone);
    const normalizedIntensity = intensity / (threshold - deadZone);
    let speed = normalizedIntensity * normalizedIntensity * maxSpeed;

    // Turbo mode: extra speed boost when very close to edge
    if (distance < turboZone) {
      speed *= turboMultiplier;
    }

    return speed;
  }, []);

  const isVelocityTooHigh = useCallback((
    point: { x: number; y: number },
    now: number,
    threshold: number
  ): boolean => {
    const lastVelocity = lastVelocityRef.current;
    const deltaTime = now - lastVelocity.time;

    if (deltaTime <= 0) return false;

    const velocityX = Math.abs(point.x - lastVelocity.x) / deltaTime;
    const velocityY = Math.abs(point.y - lastVelocity.y) / deltaTime;
    const totalVelocity = Math.sqrt(velocityX * velocityX + velocityY * velocityY);

    lastVelocityRef.current = { x: point.x, y: point.y, time: now };

    return totalVelocity > threshold;
  }, []);

  const smartPan = useCallback((lngLat: { lng: number; lat: number }) => {
    if (!mapRef?.current || !isDragging) return;

    const now = Date.now();
    const map = mapRef.current.getMap();
    const point = map.project([lngLat.lng, lngLat.lat]);

    // Skip auto-pan during fast movement
    if (isVelocityTooHigh(point, now, panSettings.velocityThreshold)) {
      return;
    }

    // High-frequency throttling for responsive updates
    if (now - lastPanTimeRef.current < panSettings.throttleMs) return;
    lastPanTimeRef.current = now;

    const container = map.getContainer();
    const containerRect = container.getBoundingClientRect();

    // Calculate distances from edges
    // Desktop: account for form panel on the right side
    // Mobile: account for minimized panel bar at bottom
    const panelWidth = getPanelWidth();
    const panelHeight = getPanelHeight();
    const effectiveWidth = panelWidth > 0 ? containerRect.width - panelWidth : containerRect.width;
    const effectiveHeight = panelHeight > 0 ? containerRect.height - panelHeight : containerRect.height;
    const distanceFromLeft = point.x;
    const distanceFromRight = effectiveWidth - point.x;
    const distanceFromTop = point.y;
    const distanceFromBottom = effectiveHeight - point.y;

    // Calculate pan speeds for each direction
    const panX = distanceFromLeft < panSettings.edgeThreshold
      ? -calculatePanSpeed(
          distanceFromLeft,
          panSettings.edgeThreshold,
          panSettings.deadZone,
          panSettings.maxPanSpeed,
          panSettings.turboZone,
          panSettings.turboMultiplier
        )
      : distanceFromRight < panSettings.edgeThreshold
      ? calculatePanSpeed(
          distanceFromRight,
          panSettings.edgeThreshold,
          panSettings.deadZone,
          panSettings.maxPanSpeed,
          panSettings.turboZone,
          panSettings.turboMultiplier
        )
      : 0;

    const panY = distanceFromTop < panSettings.edgeThreshold
      ? -calculatePanSpeed(
          distanceFromTop,
          panSettings.edgeThreshold,
          panSettings.deadZone,
          panSettings.maxPanSpeed,
          panSettings.turboZone,
          panSettings.turboMultiplier
        )
      : distanceFromBottom < panSettings.edgeThreshold
      ? calculatePanSpeed(
          distanceFromBottom,
          panSettings.edgeThreshold,
          panSettings.deadZone,
          panSettings.maxPanSpeed,
          panSettings.turboZone,
          panSettings.turboMultiplier
        )
      : 0;

    // Apply panning if needed
    if (Math.abs(panX) > 0.1 || Math.abs(panY) > 0.1) {
      if (panFrameRef.current) {
        cancelAnimationFrame(panFrameRef.current);
      }

      panFrameRef.current = requestAnimationFrame(() => {
        map.panBy([panX, panY], { duration: 0 });
      });
    }
  }, [mapRef, isDragging, isVelocityTooHigh, calculatePanSpeed, panSettings, getPanelWidth, getPanelHeight]);

  const initializeVelocityTracking = useCallback((coordinates: { lng: number; lat: number }) => {
    if (mapRef?.current) {
      const map = mapRef.current.getMap();
      const point = map.project([coordinates.lng, coordinates.lat]);
      lastVelocityRef.current = { x: point.x, y: point.y, time: Date.now() };
    }
  }, [mapRef]);

  const stopAutoPan = useCallback(() => {
    if (panFrameRef.current) {
      cancelAnimationFrame(panFrameRef.current);
      panFrameRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (panFrameRef.current) {
        cancelAnimationFrame(panFrameRef.current);
      }
    };
  }, []);

  return {
    smartPan,
    initializeVelocityTracking,
    stopAutoPan,
  };
};